import React from "react";
import { Link } from "react-router-dom";

const PrivacyPolicy = () => {
  return (
    <div className="content">
      <h2>
        <Link to="/">Home</Link> / Privacy Policy
      </h2>
      <br />

      <p align="center">
        <b>ShareMy Privacy Policy</b>
      </p>
      <p>
        <b>Effective Date: July 21, 2026</b>
      </p>

      <ul>
        <li>
          <a href="#section1">1. Introduction</a>
        </li>
        <li>
          <a href="#section2">2. Information You Provide and We Collect</a>
        </li>
        <li>
          <a href="#section3">3. Information We Automatically Collect</a>
        </li>
        <li>
          <a href="#section4">4. How We Use Your Information</a>
        </li>
        <li>
          <a href="#section5">5. When We Share Your Information</a>
        </li>
        <li>
          <a href="#section6">
            6. Third-Party Service Providers and Integrations
          </a>
        </li>
        <li>
          <a href="#section7">7. Your Privacy Rights and Choices</a>
        </li>
        <li>
          <a href="#section8">8. Account Deletion and Data Retention</a>
        </li>
        <li>
          <a href="#section9">9. Security of Your Information</a>
        </li>
        <li>
          <a href="#section10">10. Adults-Only Services</a>
        </li>
        <li>
          <a href="#section11">11. Changes to This Privacy Policy</a>
        </li>
        <li>
          <a href="#section12">12. Contact Us</a>
        </li>
        <li>
          <a href="#section13">13. U.S. State Privacy Rights</a>
        </li>
      </ul>

      <p id="section1">
        <b>1. Introduction</b>
      </p>
      <p>Welcome to ShareMy.</p>
      <p>
        ShareMy Inc. ("ShareMy," "we," "our," or "us") operates a social
        activity platform designed to help people discover activities, connect
        with others, build friendships, organize events, and participate in
        communities through our mobile applications, website, and related
        services (collectively, the "Services").
      </p>
      <p>
        This Privacy Policy explains how we collect, use, disclose, store, and
        otherwise process your information when you use the Services. It also
        explains the choices and controls available to you regarding your
        personal information.
      </p>
      <p>
        ShareMy is designed to give users meaningful control over the personal
        information they choose to provide and share. Many features of the
        Services allow you to decide whether to provide optional information
        and, when applicable, whether that information is visible to All ShareMy
        users or only to your Friends.
      </p>
      <p>
        Some information is required to create and maintain your account and
        provide the Services. Other information is entirely optional and may be
        added, modified, or removed by you at any time, subject to the
        functionality of the Services.
      </p>
      <p>
        Because ShareMy helps users discover activities and connect with one
        another, certain information that you choose to share may be visible to
        other users according to your selected privacy settings. Other
        information, including your account credentials, payment information,
        and certain account records, is not publicly displayed through your
        profile.
      </p>
      <p>This Privacy Policy applies to all ShareMy Services, including:</p>
      <ul>
        <li>Our mobile applications;</li>
        <li>Our website;</li>
        <li>Customer support;</li>
        <li>Communications relating to the Services; and</li>
        <li>
          Any other products or services that reference this Privacy Policy.
        </li>
      </ul>
      <p>
        By creating an account or using the Services, you acknowledge that you
        have read and understood this Privacy Policy and consent to the
        collection, use, disclosure, and processing of your information as
        described herein.
      </p>
      <p>This Privacy Policy should be read together with our:</p>
      <ul>
        <li>
          <Link to="/terms-of-use">Terms of Use</Link>
        </li>
        <li>
          <Link to="/community-guide">Community Guidelines</Link>
        </li>
        <li>
          <Link to="/safety-guide">Safety Guidelines</Link>
        </li>
        <li>
          <Link to="/cookie-policy">Cookie Policy</Link>
        </li>
      </ul>
      <p>
        Each document governs different aspects of your relationship with
        ShareMy.
      </p>
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in the Services, changes in applicable law, or other operational or
        legal reasons. When we make material changes, we will revise the
        Effective Date above and provide notice as required by applicable law.
      </p>

      <p id="section2">
        <b>2. Information You Provide and We Collect</b>
      </p>
      <p>
        The information we collect depends on how you use the Services, the
        features you choose to use, the information you voluntarily provide, and
        the privacy settings you select.
      </p>
      <p>
        Some information is required to create and maintain your ShareMy
        account. Most profile information beyond what is required is optional
        and is provided solely at your discretion.
      </p>
      <p>
        Unless otherwise stated, you may update, modify, or remove optional
        profile information at any time through the Services. The visibility of
        optional profile information is governed by the privacy controls
        described in Section 2.E &ndash; Privacy Controls for Your Profile.
      </p>

      <p>
        <b>A. Required Account Information</b>
      </p>
      <p>
        When you create a ShareMy account, we collect information reasonably
        necessary to establish, secure, and maintain your account.
      </p>
      <p>This information may include:</p>
      <ul>
        <li>Full name</li>
        <li>Username</li>
        <li>Email address</li>
        <li>Mobile telephone number</li>
        <li>
          Password or authentication credentials provided through Apple Sign-In
          or Google Sign-In
        </li>
        <li>Date of birth or age verification, when required</li>
        <li>Profile photograph, if you choose to upload one</li>
      </ul>
      <p>We use this information to:</p>
      <ul>
        <li>Create and manage your account.</li>
        <li>Verify your identity.</li>
        <li>Authenticate your access to the Services.</li>
        <li>Communicate with you regarding your account.</li>
        <li>Provide customer support.</li>
        <li>Operate the Services.</li>
        <li>Support the security and integrity of the Services.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>

      <p>
        <b>B. Optional Profile Information</b>
      </p>
      <p>
        ShareMy allows you to personalize your profile to help match you with
        activities, groups, and other users who share similar interests, skills,
        experience levels, and activity preferences. The information you choose
        to include in your profile also helps other users learn about you and
        decide whether to connect or participate in activities with you.
      </p>
      <p>You may voluntarily choose to provide information such as:</p>
      <ul>
        <li>Biography or personal description</li>
        <li>Interests and hobbies</li>
        <li>Favorite activities</li>
        <li>Activity experience levels</li>
        <li>Skills and certifications</li>
        <li>Languages spoken</li>
        <li>Occupation</li>
        <li>Education</li>
        <li>General location or home city</li>
        <li>Social media links, if available</li>
        <li>Other profile information you choose to provide</li>
      </ul>
      <p>Providing this information is entirely optional.</p>
      <p>
        The visibility of optional profile information is controlled through the
        privacy settings described in Section 2.E &ndash; Privacy Controls for
        Your Profile.
      </p>

      <p>
        <b>C. Optional Health and Activity Information</b>
      </p>
      <p>
        To improve activity matching and recommendations, ShareMy allows you to
        voluntarily provide information relating to your interests, abilities,
        and experience.
      </p>
      <p>Examples include:</p>
      <ul>
        <li>Fitness level</li>
        <li>Physical conditioning</li>
        <li>Activity ability level</li>
        <li>Outdoor experience</li>
        <li>Hiking experience</li>
        <li>Backpacking experience</li>
        <li>Camping experience</li>
        <li>Climbing experience</li>
        <li>Cycling experience</li>
        <li>Running experience</li>
        <li>Skiing or snowboarding experience</li>
        <li>Scuba diving or snorkeling experience</li>
        <li>Rafting, kayaking, canoeing, or paddle sports experience</li>
        <li>Hunting or fishing experience</li>
        <li>Accessibility preferences</li>
        <li>Physical limitations you voluntarily choose to disclose</li>
      </ul>
      <p>Providing this information is entirely optional.</p>
      <p>
        The visibility of this information is controlled through the privacy
        settings described in Section 2.E &ndash; Privacy Controls for Your
        Profile.
      </p>

      <p>
        <b>D. Optional Sensitive Profile Information</b>
      </p>
      <p>
        Some users may choose to include additional personal information to
        better express who they are or to help connect with users who share
        similar backgrounds, interests, or communities.
      </p>
      <p>
        Where available within the Services, you may voluntarily choose to
        provide information such as:
      </p>
      <ul>
        <li>Religion</li>
        <li>Race</li>
        <li>Ethnicity</li>
        <li>Cultural background</li>
        <li>Gender identity</li>
        <li>Sexual orientation</li>
        <li>Height</li>
        <li>Weight</li>
        <li>
          Other demographic or personal characteristics you choose to provide
        </li>
      </ul>
      <p>Providing this information is completely optional.</p>
      <p>
        ShareMy does not require this information to create or maintain an
        account.
      </p>
      <p>
        The visibility of optional sensitive profile information is controlled
        through the privacy settings described in Section 2.E &ndash; Privacy
        Controls for Your Profile.
      </p>

      <p>
        <b>E. Privacy Controls for Your Profile</b>
      </p>
      <p>
        One of ShareMy's guiding principles is to give users meaningful control
        over the personal information they choose to share.
      </p>
      <p>
        Most profile fields within the Services are optional. If you choose not
        to share certain information, you may simply leave the field blank or
        remove it from your profile.
      </p>
      <p>
        If you choose to provide optional profile information, you must also
        choose one of the following visibility settings for each supported
        profile field:
      </p>
      <p>
        <b>All</b>
      </p>
      <p>
        Information designated as All is visible to other ShareMy users who are
        permitted to view your profile. Selecting this option may help other
        users discover shared interests, activities, skills, and experiences.
      </p>
      <p>
        <b>Friends</b>
      </p>
      <p>
        Information designated as Friends is visible only to users you have
        accepted as friends within ShareMy.
      </p>
      <p>
        Optional profile information cannot be designated as visible only to
        yourself. If you prefer not to share optional profile information with
        other users, simply leave the field blank or remove the information from
        your profile.
      </p>
      <p>
        You may change the visibility of supported optional profile fields
        between All and Friends at any time through your account or profile
        settings. Changes generally apply going forward and may not affect
        information that has already been viewed, shared, copied, or otherwise
        retained by other users before the change was made.
      </p>
      <p>
        Information you voluntarily share through private messages, group
        messages, comments, activity discussions, invitations, or other direct
        interactions with other users may remain available to those recipients
        even if you later change your profile visibility settings or remove the
        information from your profile.
      </p>
      <p>
        Your privacy settings apply only to the visibility of supported profile
        fields within the Services and do not affect information you voluntarily
        share through other features or interactions.
      </p>

      <p>
        <b>F. User-Generated Content</b>
      </p>
      <p>
        We collect the content you voluntarily create, upload, or share while
        using the Services.
      </p>
      <p>Examples include:</p>
      <ul>
        <li>Activities you create</li>
        <li>Activities you join</li>
        <li>Event information</li>
        <li>Activity descriptions</li>
        <li>Comments</li>
        <li>Reviews</li>
        <li>Ratings</li>
        <li>Photos</li>
        <li>Videos</li>
        <li>Messages</li>
        <li>Invitations</li>
        <li>Group discussions</li>
        <li>Profile updates</li>
        <li>Activity participation history</li>
        <li>Other content you voluntarily submit through the Services</li>
      </ul>
      <p>
        Depending on the feature you use and your selected privacy settings,
        this content may be visible to other users.
      </p>

      <p>
        <b>G. Communications</b>
      </p>
      <p>We collect communications you send through the Services, including:</p>
      <ul>
        <li>Private messages</li>
        <li>Group messages</li>
        <li>Invitations</li>
        <li>Customer support requests</li>
        <li>Feedback</li>
        <li>Survey responses</li>
        <li>Reports submitted through moderation or reporting tools</li>
      </ul>
      <p>We use these communications to:</p>
      <ul>
        <li>Operate the Services.</li>
        <li>Facilitate communications between users.</li>
        <li>Respond to your requests.</li>
        <li>Improve the Services.</li>
        <li>
          Investigate reports of suspected violations of our Terms of Use,
          Community Guidelines, or Safety Guidelines.
        </li>
        <li>Support the security and integrity of the Services.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>

      <p id="section3">
        <b>3. Information We Automatically Collect</b>
      </p>
      <p>
        When you use the Services, certain information is collected
        automatically through your device, browser, operating system, network
        connection, and your interactions with the Services. This information
        helps us operate, maintain, improve, and support the functionality of
        ShareMy.
      </p>

      <p>
        <b>A. Device Information</b>
      </p>
      <p>
        We may automatically collect information about the device you use to
        access the Services, including:
      </p>
      <ul>
        <li>Device type</li>
        <li>Device model</li>
        <li>Operating system and version</li>
        <li>Browser type and version</li>
        <li>Device language</li>
        <li>Screen resolution</li>
        <li>Mobile carrier, when applicable</li>
        <li>Unique device identifiers</li>
        <li>Push notification tokens</li>
      </ul>
      <p>
        We use this information to improve compatibility, troubleshoot technical
        issues, optimize performance, and support the operation of the Services.
      </p>

      <p>
        <b>B. Usage Information</b>
      </p>
      <p>
        We automatically collect information about how you interact with the
        Services, including:
      </p>
      <ul>
        <li>Features you use</li>
        <li>Pages or screens you visit</li>
        <li>Activities you browse or join</li>
        <li>Searches you perform</li>
        <li>Buttons and links you select</li>
        <li>Time spent using the Services</li>
        <li>Dates and times of access</li>
        <li>Navigation patterns</li>
        <li>Referral sources</li>
      </ul>
      <p>
        This information helps us understand how the Services are used so we can
        improve functionality, usability, and overall performance.
      </p>

      <p>
        <b>C. Location Information</b>
      </p>
      <p>
        Location is an important part of the ShareMy experience because it helps
        users discover nearby activities and connect with other users.
      </p>
      <p>
        Depending on your device settings and the permissions you grant, ShareMy
        may collect:
      </p>
      <ul>
        <li>
          Approximate location derived from your Internet Protocol (IP) address.
        </li>
        <li>
          Precise GPS location when you grant permission through your device.
        </li>
      </ul>
      <p>
        You control whether ShareMy has access to your device's precise location
        through your device's permission settings. If you choose not to grant
        location access, certain location-based features may be unavailable or
        may not function as intended.
      </p>
      <p>
        ShareMy may use mapping and location services provided by Google Maps or
        other mapping providers to support location-based features. Your use of
        those services may also be subject to the applicable third-party
        provider's terms and privacy practices.
      </p>

      <p>
        <b>D. Log Information</b>
      </p>
      <p>Our systems automatically generate logs when you use the Services.</p>
      <p>These logs may include:</p>
      <ul>
        <li>Internet Protocol (IP) address</li>
        <li>Date and time of access</li>
        <li>Device identifiers</li>
        <li>Browser information</li>
        <li>Operating system information</li>
        <li>Pages requested</li>
        <li>Error reports</li>
        <li>Crash information</li>
        <li>System activity</li>
      </ul>
      <p>
        We use log information to diagnose technical issues, monitor system
        performance, investigate unauthorized activity, and support the
        operation and integrity of the Services.
      </p>

      <p>
        <b>E. Cookies and Similar Technologies</b>
      </p>
      <p>
        Our website and certain portions of the Services may use cookies and
        similar technologies to:
      </p>
      <ul>
        <li>Remember your preferences.</li>
        <li>Maintain your session.</li>
        <li>Improve website functionality.</li>
        <li>Analyze website usage.</li>
        <li>Measure performance.</li>
        <li>Support the operation of the Services.</li>
      </ul>
      <p>
        For additional information regarding cookies and similar technologies,
        please see our <Link to="/cookie-policy">Cookie Policy</Link>.
      </p>

      <p>
        <b>F. Analytics and Diagnostics</b>
      </p>
      <p>
        We use analytics and diagnostic tools to better understand how users
        interact with the Services and to improve reliability, performance, and
        usability.
      </p>
      <p>Information collected may include:</p>
      <ul>
        <li>App usage statistics</li>
        <li>Feature usage</li>
        <li>Session duration</li>
        <li>Performance metrics</li>
        <li>Crash reports</li>
        <li>Diagnostic information</li>
        <li>Aggregate usage trends</li>
      </ul>
      <p>
        ShareMy currently uses Google Firebase to help monitor application
        performance, diagnose crashes, analyze aggregated usage patterns, and
        improve the Services.
      </p>
      <p>
        Whenever reasonably practical, analytics information is aggregated or
        de-identified before being used for reporting and product improvement.
      </p>

      <p>
        <b>G. Service Integrity</b>
      </p>
      <p>We may automatically collect and analyze information to help:</p>
      <ul>
        <li>Detect suspicious or fraudulent activity.</li>
        <li>Identify spam or automated abuse.</li>
        <li>
          Investigate reported violations of our Terms of Use, Community
          Guidelines, or Safety Guidelines.
        </li>
        <li>Diagnose technical problems.</li>
        <li>Support the reliability and integrity of the Services.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>
      <p>
        No online service can eliminate every instance of fraud, abuse, or
        unauthorized activity. The information we collect helps us operate and
        improve the Services and respond appropriately when issues arise.
      </p>

      <p id="section4">
        <b>4. How We Use Your Information</b>
      </p>
      <p>
        ShareMy uses the information you provide and the information we
        automatically collect to operate, improve, and support the Services.
      </p>
      <p>
        We use your information only for purposes consistent with this Privacy
        Policy, our Terms of Use, and applicable law.
      </p>

      <p>
        <b>A. To Provide the Services</b>
      </p>
      <p>We use your information to:</p>
      <ul>
        <li>Create and manage your account.</li>
        <li>Display your profile according to your privacy settings.</li>
        <li>
          Enable you to create, organize, join, and participate in activities.
        </li>
        <li>Facilitate messaging and communications.</li>
        <li>Process friend requests and user interactions.</li>
        <li>Provide customer support.</li>
        <li>Operate and maintain the Services.</li>
      </ul>

      <p>
        <b>B. To Match You with Activities and Other Users</b>
      </p>
      <p>
        One of ShareMy's primary purposes is to help users discover activities
        and connect with people who share similar interests, skills, experience
        levels, and activity preferences.
      </p>
      <p>
        To accomplish this, ShareMy may use the information you choose to
        provide, together with your interactions within the Services, to
        identify and suggest compatible activities, groups, and users.
      </p>
      <p>
        Depending on the features you use, we may consider information such as:
      </p>
      <ul>
        <li>Interests and hobbies</li>
        <li>Favorite activities</li>
        <li>Activity experience</li>
        <li>Skills and certifications</li>
        <li>General location</li>
        <li>Activities you create, join, save, or express interest in</li>
        <li>Friend connections</li>
        <li>Profile preferences</li>
        <li>Other profile information you voluntarily provide</li>
      </ul>
      <p>Using this information, ShareMy may automatically:</p>
      <ul>
        <li>Suggest activities that may interest you.</li>
        <li>Suggest users with similar interests or activity preferences.</li>
        <li>Recommend activity partners or participants.</li>
        <li>Recommend groups and communities.</li>
        <li>Recommend nearby activities.</li>
        <li>Prioritize search results that may be more relevant to you.</li>
        <li>
          Improve future matches and recommendations based on your interactions
          with the Services.
        </li>
      </ul>
      <p>
        These recommendations are generated through automated systems designed
        to improve your experience. They are intended as suggestions only and do
        not guarantee compatibility, participation, friendships, attendance, or
        any particular outcome.
      </p>

      <p>
        <b>C. To Personalize Your Experience</b>
      </p>
      <p>We may use your information to personalize your experience by:</p>
      <ul>
        <li>Customizing content displayed within the Services.</li>
        <li>Improving search results.</li>
        <li>Displaying relevant activities.</li>
        <li>Remembering your preferences.</li>
        <li>Tailoring notifications.</li>
        <li>Improving navigation.</li>
        <li>Providing a more relevant user experience.</li>
      </ul>

      <p>
        <b>D. To Facilitate Communications</b>
      </p>
      <p>
        We use your information to support communications within the Services,
        including:
      </p>
      <ul>
        <li>Private messaging</li>
        <li>Group messaging</li>
        <li>Invitations</li>
        <li>Activity notifications</li>
        <li>Friend requests</li>
        <li>Customer support communications</li>
      </ul>
      <p>
        ShareMy currently uses Sendbird to provide in-app messaging and related
        communication services.
      </p>

      <p>
        <b>E. To Authenticate Your Account</b>
      </p>
      <p>
        Depending on how you register, authentication may be provided through:
      </p>
      <ul>
        <li>Apple Sign-In</li>
        <li>Google Sign-In</li>
        <li>ShareMy account credentials</li>
      </ul>
      <p>
        These services help verify your identity and provide secure access to
        your account.
      </p>

      <p>
        <b>F. To Process Purchases and Subscriptions</b>
      </p>
      <p>
        If you purchase premium features or other paid Services, we use your
        information to process and manage those transactions.
      </p>
      <p>Depending on how you subscribe:</p>
      <ul>
        <li>
          Mobile app subscriptions may be managed through RevenueCat, working
          with the Apple App Store or Google Play.
        </li>
        <li>
          Purchases made through the ShareMy website may be processed through
          Stripe.
        </li>
      </ul>
      <p>
        ShareMy does not receive or store your complete payment card
        information.
      </p>

      <p>
        <b>G. To Communicate With You</b>
      </p>
      <p>We may communicate with you regarding:</p>
      <ul>
        <li>Your account</li>
        <li>Activities</li>
        <li>Friend requests</li>
        <li>Messages</li>
        <li>Customer support</li>
        <li>Service announcements</li>
        <li>Security-related notifications</li>
        <li>Technical notices</li>
        <li>Updates to the Services</li>
        <li>Changes to our policies</li>
      </ul>
      <p>
        Where required by law, promotional communications will be sent only with
        the appropriate consent, and you may opt out of marketing communications
        at any time.
      </p>

      <p>
        <b>H. To Improve the Services</b>
      </p>
      <p>
        We continually evaluate how users interact with ShareMy so we can
        improve the Services.
      </p>
      <p>We may use information to:</p>
      <ul>
        <li>Improve existing features.</li>
        <li>Develop new features.</li>
        <li>Improve activity and user matching.</li>
        <li>Improve recommendation systems.</li>
        <li>Improve search functionality.</li>
        <li>Evaluate user engagement.</li>
        <li>Improve reliability and performance.</li>
        <li>Diagnose technical issues.</li>
      </ul>

      <p>
        <b>I. To Support the Operation and Integrity of the Services</b>
      </p>
      <p>We may use information to:</p>
      <ul>
        <li>Detect and investigate suspected fraud or abuse.</li>
        <li>
          Respond to violations of our Terms of Use, Community Guidelines, or
          Safety Guidelines.
        </li>
        <li>Protect the operation and integrity of the Services.</li>
        <li>Protect ShareMy's legal rights and property.</li>
        <li>Comply with applicable legal obligations.</li>
      </ul>
      <p>
        These activities are intended to support the reliable operation of the
        Services and the enforcement of our policies.
      </p>

      <p>
        <b>J. Aggregated and De-Identified Information</b>
      </p>
      <p>
        We may aggregate or de-identify information so that it can no longer
        reasonably be associated with an identifiable individual.
      </p>
      <p>
        We may use aggregated or de-identified information for purposes
        including:
      </p>
      <ul>
        <li>Research</li>
        <li>Product development</li>
        <li>Statistical reporting</li>
        <li>Performance analysis</li>
        <li>Business planning</li>
        <li>Improving the Services</li>
      </ul>
      <p>
        Information that has been aggregated or de-identified is not intended to
        identify any individual user.
      </p>

      <p>
        <b>K. Artificial Intelligence and Automated Features</b>
      </p>
      <p>
        ShareMy may use automated technologies, including artificial
        intelligence ("AI"), to enhance and improve the Services.
      </p>
      <p>These technologies may be used to:</p>
      <ul>
        <li>Improve activity matching.</li>
        <li>Improve user matching.</li>
        <li>Improve recommendations.</li>
        <li>Organize and categorize information.</li>
        <li>Enhance search functionality.</li>
        <li>Assist with content moderation.</li>
        <li>Detect spam or automated abuse.</li>
        <li>Assist customer support.</li>
        <li>Improve the overall user experience.</li>
      </ul>
      <p>
        Where automated technologies are used, ShareMy remains responsible for
        handling your information in accordance with this Privacy Policy and
        applicable law.
      </p>
      <p>
        ShareMy does not use optional sensitive profile information, by itself,
        to make decisions that produce legal or similarly significant effects
        concerning you.
      </p>

      <p id="section5">
        <b>5. When We Share Your Information</b>
      </p>
      <p>
        ShareMy may share your information only in the limited circumstances
        described in this Privacy Policy. We do not share your personal
        information except as reasonably necessary to operate the Services,
        provide features you request, comply with legal obligations, protect our
        rights, or with your consent or direction.
      </p>
      <p>
        ShareMy does not sell your personal information as described in this
        Privacy Policy.
      </p>

      <p>
        <b>A. Information You Choose to Share with Other Users</b>
      </p>
      <p>
        ShareMy is designed to help users discover activities and connect with
        other users.
      </p>
      <p>
        Information you choose to include in your profile may be visible to
        other users based on the privacy settings you select.
      </p>
      <p>
        For supported profile fields, you choose whether the information is
        visible to:
      </p>
      <ul>
        <li>All ShareMy users; or</li>
        <li>Your Friends.</li>
      </ul>
      <p>
        Information you voluntarily share through activities, messages,
        comments, invitations, group discussions, or other interactions may also
        be visible to the users participating in those interactions.
      </p>
      <p>
        You are responsible for deciding what information you choose to share
        through the Services.
      </p>

      <p>
        <b>B. Service Providers</b>
      </p>
      <p>
        ShareMy works with carefully selected third-party service providers that
        perform services on our behalf.
      </p>
      <p>
        These providers may receive access only to the information reasonably
        necessary to perform the services we request.
      </p>
      <p>Examples include providers that assist with:</p>
      <ul>
        <li>User authentication</li>
        <li>Cloud hosting</li>
        <li>Data storage</li>
        <li>Messaging</li>
        <li>Analytics</li>
        <li>Crash reporting</li>
        <li>Mapping and location services</li>
        <li>Payment processing</li>
        <li>Subscription management</li>
        <li>Customer support</li>
        <li>Security monitoring</li>
        <li>Technical infrastructure</li>
      </ul>
      <p>
        These providers are authorized to use your information only to provide
        services to ShareMy or as otherwise permitted by applicable law.
      </p>
      <p>
        Additional information regarding these providers is available in Section
        6 &ndash; Third-Party Services.
      </p>

      <p>
        <b>C. Purchases and Payment Processing</b>
      </p>
      <p>
        If you purchase premium features or other paid Services, information
        necessary to process those transactions may be shared with payment
        processors and subscription providers.
      </p>
      <p>Depending on how you purchase the Services, this may include:</p>
      <ul>
        <li>Apple App Store</li>
        <li>Google Play</li>
        <li>RevenueCat</li>
        <li>Stripe</li>
      </ul>
      <p>
        Payment transactions are subject to the privacy policies and terms of
        the applicable payment provider.
      </p>
      <p>
        ShareMy does not receive or store your complete payment card
        information.
      </p>

      <p>
        <b>D. Authentication Providers</b>
      </p>
      <p>
        If you choose to sign in using Apple Sign-In or Google Sign-In, certain
        account information is shared with the applicable authentication
        provider to verify your identity and facilitate secure access to your
        ShareMy account.
      </p>

      <p>
        <b>E. Legal Requirements</b>
      </p>
      <p>
        We may disclose information when we reasonably believe disclosure is
        necessary to:
      </p>
      <ul>
        <li>Comply with applicable laws or legal process.</li>
        <li>Respond to lawful requests from governmental authorities.</li>
        <li>Enforce our Terms of Use or other policies.</li>
        <li>Protect the rights, property, or legal interests of ShareMy.</li>
        <li>Investigate suspected violations of law or our policies.</li>
        <li>Respond to claims or disputes.</li>
        <li>Comply with court orders or other legal obligations.</li>
      </ul>

      <p>
        <b>F. Business Transactions</b>
      </p>
      <p>
        If ShareMy is involved in a merger, acquisition, financing, asset sale,
        reorganization, bankruptcy, or other business transaction, your
        information may be transferred as part of that transaction, subject to
        applicable law.
      </p>
      <p>
        If such a transaction results in a material change to the way your
        personal information is handled, we will provide notice as required by
        applicable law.
      </p>

      <p>
        <b>G. With Your Consent or Direction</b>
      </p>
      <p>
        We may share your information when you request or direct us to do so, or
        when you otherwise provide your consent.
      </p>
      <p>Examples include:</p>
      <ul>
        <li>Connecting with third-party services.</li>
        <li>Participating in optional integrations.</li>
        <li>Sharing information you specifically authorize us to disclose.</li>
      </ul>

      <p>
        <b>H. Aggregated or De-Identified Information</b>
      </p>
      <p>
        We may share aggregated or de-identified information that cannot
        reasonably be used to identify you.
      </p>
      <p>Such information may be used for:</p>
      <ul>
        <li>Research</li>
        <li>Product development</li>
        <li>Statistical analysis</li>
        <li>Business planning</li>
        <li>Improving the Services</li>
      </ul>

      <p>
        <b>I. No Guarantee Regarding Information Shared by Users</b>
      </p>
      <p>
        Once you voluntarily share information with other users through your
        profile, activities, messages, comments, invitations, or other
        interactions, those users may retain, copy, capture, or further disclose
        that information.
      </p>
      <p>
        Although ShareMy provides privacy controls for supported profile fields,
        we cannot control or guarantee how other users may use information that
        you choose to share with them.
      </p>
      <p>
        We encourage you to carefully consider what information you share
        through the Services and to use the privacy settings available to you.
      </p>

      <p id="section6">
        <b>6. Third-Party Service Providers and Integrations</b>
      </p>
      <p>
        To operate and improve the Services, ShareMy works with selected
        third-party service providers that perform specific functions on our
        behalf.
      </p>
      <p>
        These providers receive only the information reasonably necessary to
        perform the services we request or as otherwise permitted by applicable
        law.
      </p>
      <p>
        Your use of services provided by these third parties may also be subject
        to their own privacy policies, terms of service, and other agreements.
        ShareMy does not control the privacy practices of independent
        third-party providers.
      </p>
      <p>
        The following are examples of service providers currently used by
        ShareMy.
      </p>

      <p>
        <b>A. Apple Sign-In</b>
      </p>
      <p>
        Users may choose to authenticate their ShareMy account using Apple
        Sign-In.
      </p>
      <p>
        When you use Apple Sign-In, Apple provides ShareMy with information
        necessary to authenticate your identity and create or access your
        account.
      </p>
      <p>
        Depending on your Apple account settings, this may include information
        such as:
      </p>
      <ul>
        <li>Your name</li>
        <li>Email address (or Apple's private relay email address)</li>
        <li>A unique Apple account identifier</li>
      </ul>
      <p>
        Your use of Apple Sign-In is subject to Apple's privacy policy and
        terms.
      </p>

      <p>
        <b>B. Google Sign-In</b>
      </p>
      <p>
        Users may choose to authenticate their ShareMy account using Google
        Sign-In.
      </p>
      <p>
        When you use Google Sign-In, Google provides ShareMy with information
        necessary to authenticate your identity and create or access your
        account.
      </p>
      <p>Depending on your Google account settings, this may include:</p>
      <ul>
        <li>Your name</li>
        <li>Email address</li>
        <li>Profile image</li>
        <li>A unique Google account identifier</li>
      </ul>
      <p>
        Your use of Google Sign-In is subject to Google's privacy policy and
        terms.
      </p>

      <p>
        <b>C. Google Maps</b>
      </p>
      <p>
        ShareMy uses Google Maps and related mapping technologies to support
        location-based features.
      </p>
      <p>These services may assist with:</p>
      <ul>
        <li>Displaying maps</li>
        <li>Activity locations</li>
        <li>Navigation</li>
        <li>Geographic searches</li>
        <li>Nearby activity recommendations</li>
      </ul>
      <p>
        Information shared with Google Maps is subject to Google's privacy
        policy and applicable terms.
      </p>

      <p>
        <b>D. Firebase</b>
      </p>
      <p>
        ShareMy uses Google Firebase to help support the operation and
        improvement of the Services.
      </p>
      <p>Firebase may assist with:</p>
      <ul>
        <li>Application analytics</li>
        <li>Crash reporting</li>
        <li>Performance monitoring</li>
        <li>Diagnostics</li>
        <li>Push notifications</li>
        <li>Technical infrastructure</li>
      </ul>
      <p>
        Firebase may receive technical information about your device and
        interactions with the Services as described in this Privacy Policy.
      </p>

      <p>
        <b>E. Sendbird</b>
      </p>
      <p>
        ShareMy uses Sendbird to provide in-app messaging and communication
        features.
      </p>
      <p>Sendbird helps facilitate:</p>
      <ul>
        <li>Private messaging</li>
        <li>Group messaging</li>
        <li>Activity communications</li>
        <li>Message delivery</li>
        <li>Notifications related to messaging</li>
      </ul>
      <p>
        Information processed through Sendbird is limited to that reasonably
        necessary to provide these communication features.
      </p>

      <p>
        <b>F. RevenueCat</b>
      </p>
      <p>ShareMy uses RevenueCat to manage mobile application subscriptions.</p>
      <p>RevenueCat works with:</p>
      <ul>
        <li>Apple App Store</li>
        <li>Google Play</li>
      </ul>
      <p>
        RevenueCat helps ShareMy verify subscription status, manage premium
        features, and support subscription-related functionality.
      </p>
      <p>
        RevenueCat does not replace the payment processing performed by Apple or
        Google.
      </p>

      <p>
        <b>G. Stripe</b>
      </p>
      <p>
        For purchases made through the ShareMy website, payment processing may
        be provided by Stripe.
      </p>
      <p>Stripe processes payment transactions on behalf of ShareMy.</p>
      <p>
        ShareMy does not receive or store complete payment card information
        submitted through Stripe.
      </p>

      <p>
        <b>H. Other Service Providers</b>
      </p>
      <p>
        As ShareMy continues to develop, we may engage additional service
        providers to support the Services.
      </p>
      <p>Examples include providers offering:</p>
      <ul>
        <li>Cloud hosting</li>
        <li>Data storage</li>
        <li>Customer support</li>
        <li>Email delivery</li>
        <li>Security monitoring</li>
        <li>Fraud prevention</li>
        <li>Infrastructure management</li>
        <li>Software development tools</li>
        <li>Artificial intelligence services</li>
        <li>Other operational support</li>
      </ul>
      <p>
        We take reasonable steps to select service providers that support our
        operational needs and are authorized to process information only for the
        services they perform on our behalf or as otherwise permitted by
        applicable law.
      </p>

      <p>
        <b>I. Changes to Third-Party Providers</b>
      </p>
      <p>
        The third-party providers identified in this Privacy Policy may change
        as ShareMy grows and our operational needs evolve.
      </p>
      <p>
        We may add, replace, or discontinue service providers without prior
        notice, provided that any new providers process information in a manner
        consistent with this Privacy Policy and applicable law.
      </p>

      <p id="section7">
        <b>7. Your Privacy Rights and Choices</b>
      </p>
      <p>
        ShareMy is designed to give you meaningful control over your personal
        information. Depending on the Services you use, your location, and
        applicable law, you may have the ability to access, update, manage, or
        delete certain information associated with your account.
      </p>
      <p>
        The choices described below are intended to help you control how your
        information is collected, used, and shared while using the Services.
      </p>

      <p>
        <b>A. Managing Your Profile Information</b>
      </p>
      <p>
        You may review and update much of the information associated with your
        ShareMy account at any time through your account or profile settings.
      </p>
      <p>
        Depending on the features available within the Services, you may update
        information such as:
      </p>
      <ul>
        <li>Profile photograph</li>
        <li>Biography</li>
        <li>Interests</li>
        <li>Favorite activities</li>
        <li>Skills and certifications</li>
        <li>Activity experience</li>
        <li>Health and activity information</li>
        <li>Optional sensitive profile information</li>
        <li>Contact information</li>
        <li>Other profile information you have voluntarily provided</li>
      </ul>
      <p>
        Keeping your profile information current helps improve activity
        matching, recommendations, and your overall experience with the
        Services.
      </p>

      <p>
        <b>B. Privacy Settings</b>
      </p>
      <p>
        For supported profile fields, you may control whether optional profile
        information is visible to:
      </p>
      <ul>
        <li>All</li>
        <li>Friends</li>
      </ul>
      <p>
        You may change these privacy settings at any time through your profile
        settings.
      </p>
      <p>
        Changes generally apply prospectively and may not affect information
        that has already been viewed, copied, retained, or shared by other users
        before the change was made.
      </p>

      <p>
        <b>C. Location Permissions</b>
      </p>
      <p>
        You control whether ShareMy may access your device's precise location
        through your device's operating system settings.
      </p>
      <p>If location access is disabled:</p>
      <ul>
        <li>Nearby activity recommendations may be limited.</li>
        <li>Certain location-based features may not function as intended.</li>
        <li>
          You may need to manually enter location information for some features.
        </li>
      </ul>
      <p>
        You may enable or disable location permissions at any time through your
        device settings.
      </p>

      <p>
        <b>D. Notifications</b>
      </p>
      <p>You may choose how ShareMy communicates with you.</p>
      <p>
        Depending on the Services and your device settings, you may manage
        preferences for:
      </p>
      <ul>
        <li>Push notifications</li>
        <li>Email communications</li>
        <li>Activity notifications</li>
        <li>Friend request notifications</li>
        <li>Message notifications</li>
        <li>Marketing communications, where offered</li>
      </ul>
      <p>
        You may also control many notifications through your device's operating
        system settings.
      </p>
      <p>
        Certain communications, such as security notifications, account
        verification, password resets, subscription notices, or other important
        service-related messages, may continue to be sent when necessary to
        operate your account or comply with legal obligations.
      </p>

      <p>
        <b>E. Marketing Communications</b>
      </p>
      <p>
        Where permitted by law, ShareMy may send promotional emails or other
        marketing communications regarding new features, services, or
        promotions.
      </p>
      <p>
        You may opt out of receiving marketing communications at any time by:
      </p>
      <ul>
        <li>
          Following the unsubscribe instructions included in the communication.
        </li>
        <li>
          Updating your communication preferences within the Services, where
          available.
        </li>
        <li>
          Contacting us using the information provided in this Privacy Policy.
        </li>
      </ul>
      <p>
        Opting out of marketing communications does not affect service-related
        or transactional communications.
      </p>

      <p>
        <b>F. Accessing, Updating, or Deleting Your Information</b>
      </p>
      <p>
        Subject to applicable law, you may request access to, correction of, or
        deletion of certain personal information maintained by ShareMy.
      </p>
      <p>
        You may also be able to update or remove much of your information
        directly through your account settings.
      </p>
      <p>
        Deletion of your account may not immediately remove all information from
        our systems. We may retain certain information for a reasonable period
        of time as necessary to:
      </p>
      <ul>
        <li>Complete pending transactions.</li>
        <li>Resolve disputes.</li>
        <li>Enforce our agreements.</li>
        <li>Detect or investigate fraud or abuse.</li>
        <li>Comply with legal obligations.</li>
        <li>Maintain appropriate business records.</li>
        <li>Protect the operation and integrity of the Services.</li>
      </ul>
      <p>
        When information is no longer required for these purposes, it will be
        deleted or de-identified in accordance with our data retention
        practices.
      </p>

      <p>
        <b>G. State and Regional Privacy Rights</b>
      </p>
      <p>
        Depending on where you reside, applicable privacy laws may provide
        additional rights regarding your personal information.
      </p>
      <p>Subject to applicable law, these rights may include the ability to:</p>
      <ul>
        <li>Request access to certain personal information.</li>
        <li>Request correction of inaccurate information.</li>
        <li>Request deletion of certain personal information.</li>
        <li>Request a copy of certain information you have provided.</li>
        <li>
          Appeal certain privacy-related decisions, where required by law.
        </li>
      </ul>
      <p>
        ShareMy will respond to qualifying requests in accordance with
        applicable law.
      </p>
      <p>
        Additional information regarding privacy rights available under specific
        state or regional laws is provided later in this Privacy Policy.
      </p>

      <p>
        <b>H. Exercising Your Privacy Rights</b>
      </p>
      <p>
        If you wish to exercise any applicable privacy rights or have questions
        regarding your personal information, you may contact us using the
        contact information provided in Section 12 &ndash; Contact Us.
      </p>
      <p>
        To help protect your privacy and the security of your account, ShareMy
        may take reasonable steps to verify your identity before processing
        certain requests.
      </p>

      <p>
        <b>I. Limitations</b>
      </p>
      <p>
        Certain information may not be modified or deleted if retention is
        reasonably necessary to:
      </p>
      <ul>
        <li>Complete services you have requested.</li>
        <li>Protect the operation and integrity of the Services.</li>
        <li>Detect or investigate fraud or abuse.</li>
        <li>Comply with applicable laws.</li>
        <li>Resolve disputes.</li>
        <li>Enforce our agreements.</li>
        <li>Protect the legal rights of ShareMy or others.</li>
      </ul>
      <p>
        Where applicable, we will explain if we are unable to fulfill a request
        because of a legal or operational obligation.
      </p>

      <p id="section8">
        <b>8. Account Deletion and Data Retention</b>
      </p>
      <p>
        You may delete your ShareMy account at any time through the Services, if
        available, or by contacting ShareMy using the contact information
        provided in this Privacy Policy.
      </p>
      <p>
        Deleting your account removes your ability to access the Services using
        that account and begins the process of removing or de-identifying your
        personal information, subject to the exceptions described below.
      </p>

      <p>
        <b>A. What Happens When You Delete Your Account</b>
      </p>
      <p>When you request deletion of your account:</p>
      <ul>
        <li>Your account will be deactivated.</li>
        <li>Your public profile will no longer be visible to other users.</li>
        <li>You will no longer be able to sign in using that account.</li>
        <li>
          Active subscriptions will be handled according to the applicable app
          store or payment provider.
        </li>
        <li>
          The deletion process will begin in accordance with our data retention
          practices.
        </li>
      </ul>
      <p>
        Certain information may be removed immediately, while other information
        may remain for a reasonable period as described below.
      </p>

      <p>
        <b>B. Information We May Retain</b>
      </p>
      <p>
        Even after your account has been deleted, ShareMy may retain certain
        information for as long as reasonably necessary to:
      </p>
      <ul>
        <li>Comply with applicable laws and legal obligations.</li>
        <li>Resolve disputes.</li>
        <li>Enforce our Terms of Use and other agreements.</li>
        <li>
          Detect, investigate, or respond to fraud, abuse, or security
          incidents.
        </li>
        <li>Protect the operation and integrity of the Services.</li>
        <li>Complete financial, accounting, auditing, or tax obligations.</li>
        <li>Maintain appropriate business records.</li>
        <li>Exercise or defend legal claims.</li>
      </ul>
      <p>
        When possible, retained information will be limited to what is
        reasonably necessary for these purposes.
      </p>

      <p>
        <b>C. Messages and Shared Content</b>
      </p>
      <p>
        Deleting your account does not necessarily remove information you have
        previously shared with other users.
      </p>
      <p>For example:</p>
      <ul>
        <li>Private messages may remain visible to recipients.</li>
        <li>Group messages may remain visible to group participants.</li>
        <li>Comments may remain associated with discussions.</li>
        <li>
          Activity participation records may remain where necessary to preserve
          the integrity of conversations, activities, or historical records.
        </li>
        <li>
          Information that other users have copied, downloaded, captured, or
          otherwise retained cannot be deleted by ShareMy.
        </li>
      </ul>

      <p>
        <b>D. De-Identified and Aggregated Information</b>
      </p>
      <p>
        ShareMy may retain information that has been aggregated or de-identified
        so that it can no longer reasonably be associated with an identifiable
        individual.
      </p>
      <p>Such information may continue to be used for:</p>
      <ul>
        <li>Statistical reporting</li>
        <li>Product improvement</li>
        <li>Analytics</li>
        <li>Research</li>
        <li>Business planning</li>
        <li>Improving the Services</li>
      </ul>

      <p>
        <b>E. Backup Systems</b>
      </p>
      <p>
        Copies of your information may remain in encrypted backup systems for a
        limited period following deletion.
      </p>
      <p>
        These backups are maintained for disaster recovery, business continuity,
        and security purposes and are generally removed or overwritten in
        accordance with our normal backup retention schedules.
      </p>
      <p>
        Backup information is not ordinarily accessed except when necessary for
        operational, legal, or security purposes.
      </p>

      <p>
        <b>F. Retention Periods</b>
      </p>
      <p>
        ShareMy retains personal information only for as long as reasonably
        necessary to:
      </p>
      <ul>
        <li>Provide the Services.</li>
        <li>Fulfill the purposes described in this Privacy Policy.</li>
        <li>Comply with applicable legal obligations.</li>
        <li>Resolve disputes.</li>
        <li>Enforce our agreements.</li>
        <li>Protect the operation and integrity of the Services.</li>
      </ul>
      <p>
        Because different categories of information serve different purposes,
        retention periods may vary depending on the nature of the information
        and applicable legal requirements.
      </p>
      <p>
        When personal information is no longer reasonably necessary for these
        purposes, it will be deleted, anonymized, or de-identified in accordance
        with our data retention practices.
      </p>

      <p>
        <b>G. Reopening an Account</b>
      </p>
      <p>
        Once an account has been permanently deleted, it generally cannot be
        restored.
      </p>
      <p>
        If you later wish to use ShareMy again, you may be required to create a
        new account.
      </p>

      <p id="section9">
        <b>9. Security of Your Information</b>
      </p>
      <p>
        ShareMy uses reasonable administrative, technical, and physical
        safeguards designed to help protect the personal information we maintain
        from unauthorized access, use, alteration, disclosure, or destruction.
      </p>
      <p>These safeguards may include measures such as:</p>
      <ul>
        <li>Encryption of data in transit where appropriate.</li>
        <li>Secure authentication procedures.</li>
        <li>Access controls for authorized personnel.</li>
        <li>System monitoring.</li>
        <li>Security testing.</li>
        <li>Network and infrastructure protections.</li>
        <li>Secure data storage practices.</li>
      </ul>
      <p>
        Although we take reasonable steps to help protect your information, no
        method of transmitting information over the Internet or storing
        electronic information is completely secure.
      </p>
      <p>
        Accordingly, ShareMy cannot guarantee the absolute security of your
        information or that unauthorized access, disclosure, alteration, or
        destruction will never occur.
      </p>
      <p>You are responsible for helping protect your account by:</p>
      <ul>
        <li>
          Maintaining the confidentiality of your password or authentication
          credentials.
        </li>
        <li>Using a secure device.</li>
        <li>
          Promptly notifying ShareMy if you believe your account has been
          compromised.
        </li>
        <li>
          Logging out of shared or public devices after using the Services.
        </li>
      </ul>

      <p id="section10">
        <b>10. Adults-Only Services</b>
      </p>
      <p>
        ShareMy is intended solely for individuals who are at least eighteen
        (18) years of age.
      </p>
      <p>
        You may not create an account or use the Services if you are under the
        age of 18.
      </p>
      <p>
        By creating a ShareMy account or using the Services, you represent and
        warrant that you are at least eighteen (18) years old and have the legal
        capacity to enter into a binding agreement.
      </p>
      <p>
        ShareMy does not knowingly collect personal information from individuals
        under the age of 18.
      </p>
      <p>
        If we become aware that an account has been created by or on behalf of a
        person under 18 years of age, we may suspend or terminate the account
        and take reasonable steps to delete the associated personal information,
        subject to applicable law and our data retention practices.
      </p>
      <p>
        If you believe that someone under the age of 18 has created a ShareMy
        account or provided personal information through the Services, please
        contact us using the contact information provided below.
      </p>

      <p id="section11">
        <b>11. Changes to This Privacy Policy</b>
      </p>
      <p>
        We may update this Privacy Policy from time to time to reflect changes
        in our Services, business practices, legal requirements, or other
        operational needs.
      </p>
      <p>
        When we make changes, we will revise the Effective Date at the beginning
        of this Privacy Policy.
      </p>
      <p>
        If we make material changes, we may also provide additional notice
        through the Services, by email, or by other methods as required or
        permitted by applicable law.
      </p>
      <p>
        Your continued use of the Services after an updated Privacy Policy
        becomes effective constitutes your acknowledgment of the revised Privacy
        Policy, except where applicable law requires additional consent.
      </p>
      <p>
        We encourage you to review this Privacy Policy periodically to remain
        informed about how we collect, use, and share your information.
      </p>

      <p id="section12">
        <b>12. Contact Us</b>
      </p>
      <p>
        If you have questions about this Privacy Policy, your personal
        information, or our privacy practices, you may contact us at:
      </p>
      <p>ShareMy Inc.</p>
      <p>
        Email: <a href="mailto:support@sharemy.com">support@sharemy.com</a>
      </p>
      <p>
        Website: <a href="https://www.sharemy.com">https://www.sharemy.com</a>
      </p>
      <p>
        ShareMy Inc.
        <br />
        6444 E Old Oregon Trail Rd
        <br />
        Lava Hot Springs, Idaho, United States
      </p>
      <p>
        If you contact us regarding your personal information, we may request
        information reasonably necessary to verify your identity before
        responding to certain requests.
      </p>

      <p id="section13">
        <b>13. U.S. State Privacy Rights</b>
      </p>
      <p>
        Depending on the state in which you reside, you may have additional
        privacy rights under applicable state privacy laws.
      </p>
      <p>
        Subject to applicable law and any available exemptions, these rights may
        include the ability to:
      </p>
      <ul>
        <li>
          Request access to certain personal information we maintain about you.
        </li>
        <li>Request correction of inaccurate personal information.</li>
        <li>Request deletion of certain personal information.</li>
        <li>
          Obtain a copy of certain personal information you have provided to us
          in a portable format, where required by law.
        </li>
        <li>
          Appeal the denial of certain privacy requests, where applicable.
        </li>
        <li>Exercise your privacy rights without unlawful discrimination.</li>
      </ul>
      <p>
        The availability and scope of these rights may vary depending on your
        state of residence and applicable law.
      </p>

      <p>
        <b>A. California Residents</b>
      </p>
      <p>
        If you are a California resident, you may have additional rights under
        the California Consumer Privacy Act ("CCPA"), as amended by the
        California Privacy Rights Act ("CPRA"), including the right to:
      </p>
      <ul>
        <li>Know the categories of personal information we collect.</li>
        <li>
          Know the purposes for which we collect and use personal information.
        </li>
        <li>Request access to certain personal information.</li>
        <li>Request correction of inaccurate personal information.</li>
        <li>
          Request deletion of certain personal information, subject to
          applicable exceptions.
        </li>
        <li>
          Request information regarding certain disclosures of personal
          information.
        </li>
        <li>
          Exercise applicable privacy rights without unlawful discrimination.
        </li>
      </ul>
      <p>
        ShareMy does not sell your personal information or share your personal
        information for cross-context behavioral advertising as those terms are
        defined under California law.
      </p>

      <p>
        <b>B. Residents of Other U.S. States</b>
      </p>
      <p>
        Residents of states that provide comprehensive consumer privacy rights,
        including Virginia, Colorado, Connecticut, Utah, Oregon, Texas, Montana,
        Delaware, Iowa, Nebraska, New Hampshire, New Jersey, Tennessee,
        Minnesota, Maryland, Indiana, Kentucky, Rhode Island, and any other
        state that adopts applicable privacy legislation, may have similar
        rights under their respective state laws.
      </p>
      <p>
        The availability and scope of these rights depend upon applicable law
        and your state of residence.
      </p>

      <p>
        <b>C. Exercising Your Privacy Rights</b>
      </p>
      <p>
        To submit a privacy request, please contact ShareMy using the contact
        information provided in Section 12 &ndash; Contact Us.
      </p>
      <p>
        To help protect your privacy and the security of your account, ShareMy
        may request information reasonably necessary to verify your identity
        before responding to your request.
      </p>
      <p>
        Authorized agents may submit requests on behalf of another individual
        where permitted by applicable law. ShareMy may require documentation
        demonstrating the agent's authority and may also verify the identity of
        the individual whose information is the subject of the request.
      </p>

      <p>
        <b>D. Appeals</b>
      </p>
      <p>
        Where required by applicable law, if ShareMy denies a privacy request,
        you may submit an appeal by contacting us using the information provided
        in Section 12 &ndash; Contact Us.
      </p>
      <p>
        We will review your appeal and respond within the time required by
        applicable law.
      </p>
      <p>
        If applicable law provides additional rights to contact your state's
        attorney general or other regulatory authority regarding our response,
        information about those rights will be included in our response to your
        appeal.
      </p>

      <p>
        <b>E. Future Changes in Privacy Laws</b>
      </p>
      <p>Privacy laws continue to evolve.</p>
      <p>
        ShareMy intends to comply with applicable U.S. privacy laws that apply
        to our Services. As privacy laws change, we may update this Privacy
        Policy to reflect new legal requirements or additional rights available
        to our users.
      </p>
    </div>
  );
};

export default PrivacyPolicy;
